'use client';
import { toast, ToastOptions } from 'react-toastify';

import { useIsMobile } from '@hooks/useIsMobile';
import Accept from '@icons/Accept.svg';
import Close from '@icons/Close.svg';
import Decline from '@icons/Decline.svg';
import Info from '@icons/Info.svg';
import { lightTheme } from '@styles/theme';

import { StyledToastContainer } from '@components/ToastContainer';
import { ToastMessage } from '@components/ToastMessage';

type ToastType = 'success' | 'error' | 'info' | 'warning';

/**
 * Function to show a toast message.
 */
export const showToast = (
  type: ToastType,
  title: string,
  paragraph: string,
  options?: Partial<ToastOptions>,
) => {
  const CloseButton = ({ closeToast }: { closeToast: () => void }) => (
    <button onClick={closeToast} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
      <Close color={lightTheme.colors.typographyHeadersLight} />
    </button>
  );

  const toastOptions: ToastOptions = {
    closeButton: CloseButton,
    ...options,
  };

  const content = <ToastMessage title={title} paragraph={paragraph} />;

  switch (type) {
    case 'success':
      return toast.success(content, toastOptions);
    case 'error':
      return toast.error(content, toastOptions);
    case 'info':
      return toast.info(content, toastOptions);
    default:
      return toast(content, toastOptions);
  }
};

/**
 * Styled Toast messages container component.
 */
export const GlobalToastContainer = () => {
  const isMobile = useIsMobile();

  return (
    <StyledToastContainer
      autoClose={false}
      closeButton={false}
      position={isMobile ? 'bottom-center' : 'top-right'}
      icon={({ type }) => {
        switch (type) {
          case 'error':
            return <Decline color={lightTheme.colors.statusError} />;
          case 'success':
            return <Accept color={lightTheme.colors.statusSuccess} />;
          case 'info':
            return <Info color={lightTheme.colors.statusWarning} />;
          default:
            return null;
        }
      }}
    />
  );
};
